import React from 'react';
import { LinkedInSignalEvent } from '@/types/signals';
import { formatTime } from '@/app/lib/utils';
import { useTranslations } from 'next-intl';
import Tooltip from '../ui/Tooltip';
import Pill from '../ui/Pill';
import { getAttributionPresentation, getSignalAttribution } from './attributionPresentation';
import {
  getLinkedinActionParts,
  getLinkedInSignalEmoji,
  linkedinActionTranslationKeys,
  getLinkedInSignalDisplay,
} from './linkedinSignalPresentation';

type LinkedInSignalActivityItemProps = {
  signal: LinkedInSignalEvent;
};

const LinkedInSignalActivityItem = React.memo(({ signal }: LinkedInSignalActivityItemProps) => {
  const t = useTranslations('signals');
  const linkedinSignal = signal.linkedin_signal ?? signal.linkedin;
  const { reaction_type = '' } = linkedinSignal ?? {};
  const rawAction = signal.trigger || signal.action || linkedinSignal?.trigger || '';
  const action = rawAction;
  const actionTranslationKey = linkedinActionTranslationKeys[action];
  const actionLabel = actionTranslationKey ? t(actionTranslationKey as never) : action;
  const actionParts = getLinkedinActionParts(action, reaction_type);
  const actionEmoji = getLinkedInSignalEmoji(action, reaction_type);
  const activityAt = signal.signal_at || signal.captured_at || signal.created_at;
  const linkDisplay = getLinkedInSignalDisplay({
    action,
    signal: linkedinSignal,
  });
  const attribution = getSignalAttribution(signal);
  const attributionPresentation = getAttributionPresentation(attribution);

  return (
    <div className="flex flex-col gap-2 border-b border-border p-3 text-[11px] last:border-b-0">
      <div className="flex min-w-0 items-center gap-1">
        <span className="shrink-0 text-muted-foreground">{formatTime(activityAt)} -</span>
        <div className="flex min-w-0 flex-1 items-center gap-1">
          {(actionParts || actionLabel) && (
            <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
              {actionEmoji && <span aria-hidden="true">{actionEmoji}</span>}
              {actionParts ? (
                <>
                  <span className="font-medium text-secondary-foreground">
                    {t(actionParts.verb as never)}
                  </span>
                  {actionParts.connector && <span>{t(actionParts.connector as never)}</span>}
                </>
              ) : (
                actionLabel
              )}
            </span>
          )}
          {linkDisplay.href && (
            <Tooltip
              label={linkDisplay.href}
              side="top"
              sideOffset={3}
              delayDuration={0}
              className="max-w-[min(400px,90vw)] rounded-[6px] bg-[#333333] px-3 py-1.5 text-xs text-white [overflow-wrap:anywhere]"
              arrowClassName="fill-[#333333]"
            >
              <a
                href={linkDisplay.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={event => event.stopPropagation()}
                className="block w-fit max-w-full min-w-0 shrink truncate font-medium text-secondary-foreground underline decoration-secondary-foreground underline-offset-2 hover:text-secondary-foreground"
              >
                {linkDisplay.text}
              </a>
            </Tooltip>
          )}
          {!linkDisplay.href && linkDisplay.text && (
            <span
              className="min-w-0 flex-1 truncate font-medium text-secondary-foreground"
              title={linkDisplay.text}
            >
              {linkDisplay.text}
            </span>
          )}
        </div>
      </div>
      {attributionPresentation?.sourceName && (
        <div className="flex items-center">
          <Pill variant="outline" className="gap-1.5 px-2">
            {attributionPresentation.sourceLogo && (
              <img
                src={attributionPresentation.sourceLogo}
                alt=""
                className="size-3.5 object-contain"
                onError={e => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <span>{attributionPresentation.sourceName}</span>
          </Pill>
        </div>
      )}
    </div>
  );
});

LinkedInSignalActivityItem.displayName = 'LinkedInSignalActivityItem';

export default LinkedInSignalActivityItem;
